/**
 * @license
 * Copyright Endlessjs. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */

import { ComponentFactoryResolver, ComponentRef, Inject, Injectable } from '@angular/core';

import { ElComponentPortal, ElOverlayRef } from '../cdk/overlay/mapping';
import { ElOverlayService, patch } from '../cdk/overlay/overlay-service';
import { ElPositionBuilderService } from '../cdk/overlay/overlay-position';
import { ElGlobalLogicalPosition, ElGlobalPosition, ElPositionHelper } from '../cdk/overlay/position-helper';
import { ElToastrContainerComponent } from './toastr-container.component';
import { EL_TOASTR_CONFIG, ElToastrConfig } from './toastr-config';
import { ElToast } from './model';
import { ElToastComponent } from './toast.component';
import { EL_DOCUMENT } from '../../theme.options';

export class ElToastRef {
  constructor(private toastContainer: ElToastContainer,
              private toast: ElToast) {
  }

  close() {
    this.toastContainer.destroy(this.toast);
  }
}

export class ElToastContainer {
  protected toasts: ElToast[] = [];
  protected prevToast: ElToast;

  get nativeElement() {
    return this.containerRef.location.nativeElement;
  }

  constructor(protected position: ElGlobalPosition,
              protected containerRef: ComponentRef<ElToastrContainerComponent>,
              protected positionHelper: ElPositionHelper) {
  }

  attach(toast: ElToast): ElToastRef {
    if (toast.config.preventDuplicates && this.isDuplicate(toast)) {
      return;
    }

    this.removeToastIfLimitReached(toast);
    const toastComponent: ElToastComponent = this.attachToast(toast);

    if (toast.config.destroyByClick) {
      this.subscribeOnClick(toastComponent, toast);
    }

    if (toast.config.duration) {
      this.setDestroyTimeout(toast);
    }

    this.prevToast = toast;

    return new ElToastRef(this, toast);
  }

  destroy(toast: ElToast) {
    if (this.prevToast === toast) {
      this.prevToast = null;
    }

    this.toasts = this.toasts.filter(t => t !== toast);
    this.updateContainer();
  }

  protected isDuplicate(toast: ElToast): boolean {
    return toast.config.duplicatesBehaviour === 'previous'
      ? this.isDuplicatePrevious(toast)
      : this.isDuplicateAmongAll(toast);
  }

  protected isDuplicatePrevious(toast: ElToast): boolean {
    return this.prevToast && this.toastDuplicateCompareFunc(this.prevToast, toast);
  }

  protected isDuplicateAmongAll(toast: ElToast): boolean {
    return this.toasts.some(t => this.toastDuplicateCompareFunc(t, toast));
  }

  protected toastDuplicateCompareFunc = (t1: ElToast, t2: ElToast): boolean => {
    return t1.message === t2.message
      && t1.title === t2.title
      && t1.config.status === t2.config.status;
  };

  protected removeToastIfLimitReached(toast: ElToast) {
    if (!toast.config.limit || this.toasts.length < toast.config.limit) {
      return;
    }
    if (this.positionHelper.isTopPosition(toast.config.position)) {
      this.toasts.pop();
    } else {
      this.toasts.shift();
    }
  }

  protected attachToast(toast: ElToast): ElToastComponent {
    if (this.positionHelper.isTopPosition(toast.config.position)) {
      return this.attachToTop(toast);
    } else {
      return this.attachToBottom(toast);
    }
  }

  protected attachToTop(toast: ElToast): ElToastComponent {
    this.toasts.unshift(toast);
    this.updateContainer();
    return this.containerRef.instance.toasts.first;
  }

  protected attachToBottom(toast: ElToast): ElToastComponent {
    this.toasts.push(toast);
    this.updateContainer();
    return this.containerRef.instance.toasts.last;
  }

  protected setDestroyTimeout(toast: ElToast) {
    setTimeout(() => this.destroy(toast), toast.config.duration);
  }

  protected subscribeOnClick(toastComponent: ElToastComponent, toast: ElToast) {
    toastComponent.destroy.subscribe(() => this.destroy(toast));
  }

  protected updateContainer() {
    patch(this.containerRef, { content: this.toasts, position: this.position });
  }
}

interface ElToastrOverlayWithContainer {
  overlayRef: ElOverlayRef;
  toastrContainer: ElToastContainer;
}

@Injectable()
export class ElToastrContainerRegistry {
  protected overlays: Map<ElGlobalPosition, ElToastrOverlayWithContainer> = new Map();

  constructor(protected overlay: ElOverlayService,
              protected positionBuilder: ElPositionBuilderService,
              protected positionHelper: ElPositionHelper,
              protected cfr: ComponentFactoryResolver,
              @Inject(EL_DOCUMENT) protected document: any) {
  }

  get(position: ElGlobalPosition): ElToastContainer {
    const logicalPosition: ElGlobalLogicalPosition = this.positionHelper.toLogicalPosition(position);

    const overlayWithContainer = this.overlays.get(logicalPosition);
    if (!overlayWithContainer || !this.existsInDom(overlayWithContainer.toastrContainer)) {
      if (overlayWithContainer) {
        overlayWithContainer.overlayRef.dispose();
      }
      this.instantiateContainer(logicalPosition);
    }

    return this.overlays.get(logicalPosition).toastrContainer;
  }

  protected instantiateContainer(position: ElGlobalLogicalPosition) {
    const toastrOverlayWithContainer = this.createContainer(position);
    this.overlays.set(position, toastrOverlayWithContainer);
  }

  protected createContainer(position: ElGlobalLogicalPosition): ElToastrOverlayWithContainer {
    const positionStrategy = this.positionBuilder.global().position(position);
    const ref = this.overlay.create({ positionStrategy });
    const containerRef = ref.attach(new ElComponentPortal(ElToastrContainerComponent, null, null, this.cfr));
    return {
      overlayRef: ref,
      toastrContainer: new ElToastContainer(position, containerRef, this.positionHelper),
    };
  }

  protected existsInDom(toastContainer: ElToastContainer): boolean {
    return this.document.body.contains(toastContainer.nativeElement);
  }
}

/**
 * The `ElToastrService` provides a capability to build toast notifications.
 *
 * @stacked-example(Showcase, toastr/toastr-showcase.component)
 *
 * `ElToastrService.show(message, title, config)` accepts three params, title and config are optional.
 *
 * ### Installation
 *
 * Import `ElToastrModule.forRoot()` to your app module.
 * ```ts
 * @NgModule({
 *   imports: [
 *     // ...
 *     ElToastrModule.forRoot(config),
 *   ],
 * })
 * export class AppModule { }
 * ```
 *
 * ### Usage
 *
 * Calling `ElToastrService.show(...)` will render new toast and return `ElToastrRef` with
 * help of which you may close newly created toast by calling `close` method.
 *
 * ```ts
 * const toastRef: ElToastRef = this.toastrService.show(...);
 * toastRef.close();
 * ```
 *
 * Config accepts following options:
 *
 * `position` - determines where on the screen toast will be rendered.
 * Default is `top-end`.
 *
 * @stacked-example(Position, toastr/toastr-positions.component)
 *
 * `status` - coloring and icon of the toast.
 * Default is `primary`
 *
 * @stacked-example(Status, toastr/toastr-statuses.component)
 *
 * `duration` - the time after which the toast will be destroyed.
 * `0` means endless toast, that may be destroyed by click only.
 * Default is 3000 ms.
 *
 * @stacked-example(Duration, toastr/toastr-duration.component)
 *
 * `destroyByClick` - provides a capability to destroy toast by click.
 * Default is true.
 *
 * @stacked-example(Destroy by click, toastr/toastr-destroy-by-click.component)
 *
 * `preventDuplicates` - don't create new toast if it has the same title, message and status.
 * Default is false.
 *
 * @stacked-example(Prevent duplicates, toastr/toastr-prevent-duplicates.component)
 *
 * `duplicatesBehaviour` - determines how to threat the toasts duplication.
 * Compare with the previous message `previous`
 * or with all visible messages `all`.
 *
 * @stacked-example(Prevent duplicates behaviour , toastr/toastr-prevent-duplicates-behaviour.component)
 *
 * `limit` - the number of visible toasts in the toast container. The number of toasts is unlimited by default.
 *
 * @stacked-example(Prevent duplicates behaviour , toastr/toastr-limit.component)
 *
 * `hasIcon` - if true then render toast icon.
 * `icon` - you can pass icon class that will be applied into the toast.
 *
 * @stacked-example(Has icon, toastr/toastr-icon.component)
 * */
@Injectable()
export class ElToastrService {
  constructor(@Inject(EL_TOASTR_CONFIG) protected globalConfig: ElToastrConfig,
              protected containerRegistry: ElToastrContainerRegistry) {
  }

  /**
   * Shows toast with message, title and user config.
   * */
  show(message, title?, userConfig?: Partial<ElToastrConfig>): ElToastRef {
    const config = new ElToastrConfig({ ...this.globalConfig, ...userConfig });
    const container = this.containerRegistry.get(config.position);
    const toast = { message, title, config };
    return container.attach(toast);
  }

  /**
   * Shows success toast with message, title and user config.
   * */
  success(message, title?, config?: Partial<ElToastrConfig>): ElToastRef {
    return this.show(message, title, { ...config, status: 'success' });
  }

  /**
   * Shows info toast with message, title and user config.
   * */
  info(message, title?, config?: Partial<ElToastrConfig>): ElToastRef {
    return this.show(message, title, { ...config, status: 'info' });
  }

  /**
   * Shows warning toast with message, title and user config.
   * */
  warning(message, title?, config?: Partial<ElToastrConfig>): ElToastRef {
    return this.show(message, title, { ...config, status: 'warning' });
  }

  /**
   * Shows primary toast with message, title and user config.
   * */
  primary(message, title?, config?: Partial<ElToastrConfig>): ElToastRef {
    return this.show(message, title, { ...config, status: 'primary' });
  }

  /**
   * Shows danger toast with message, title and user config.
   * */
  danger(message, title?, config?: Partial<ElToastrConfig>): ElToastRef {
    return this.show(message, title, { ...config, status: 'danger' });
  }

  /**
   * Shows default toast with message, title and user config.
   * */
  default(message, title?, config?: Partial<ElToastrConfig>): ElToastRef {
    return this.show(message, title, { ...config, status: '' });
  }
}
